// src/features/sale/history/salePermissions.ts
//
// Permisos del Historial de Ventas. La visibilidad de cada botón la define el
// backend (tabla role_permissions); acá NO hay mapas de rol hardcodeados.
// Único caso de rol: SUPPORT es "break glass" (ve/hace todo).

export interface SalePermissions {
  /** Entrar al historial y ver el detalle de una venta. */
  canView: boolean;
  /** Ver la respuesta/rechazo que devolvió Hacienda para el DTE. */
  canViewHaciendaResponse: boolean;
  /** Descargar el PDF de la venta. */
  canViewPdf: boolean;
  /** Enviar la venta por correo. */
  canSendEmail: boolean;
  /** Consultar en Hacienda el estado del DTE. */
  canConsultDte: boolean;
  /** Descargar el JSON del DTE. */
  canViewJson: boolean;
  /** Imprimir ahora / reintentar impresión del ticket. */
  canReprint: boolean;
  /** Anular la venta (incluye anulación técnica de rechazadas). */
  canCancel: boolean;
  /** Retomar/editar una venta pendiente (mismo permiso que el POS). */
  canEdit: boolean;
  /** Editar una venta ya finalizada o con DTE desde el historial. */
  canEditFinalized: boolean;
  /** Reenviar el DTE a Hacienda. */
  canResendDte: boolean;
  /** Anulación técnica de una venta rechazada por MH. */
  canForceAnnul: boolean;
  /** Eliminar físicamente una venta rechazada. */
  canDeleteRejected: boolean;
  /** Editar una venta pendiente de caja. */
  canEditPendingCashier: boolean;
  /** Ver/editar borradores descartados (solo Soporte). */
  canSupportView: boolean;
  canSupportEdit: boolean;
  /** Crear evento de retorno (fiscal): solo ADMIN y SUPPORT. */
  canCreateReturn: boolean;
}

/**
 * Deriva los permisos del historial a partir del set del JWT.
 *
 * @param hasRole       ¿el usuario tiene este rol? (solo para SUPPORT / retorno)
 * @param hasPermission ¿el usuario tiene este permiso? (del claim `permissions`)
 */
export const getSalePermissions = (
  hasRole: (role: string) => boolean,
  hasPermission: (permission: string) => boolean = () => false,
): SalePermissions => {
  const isSupport = hasRole('SUPPORT');
  const can = (permission: string) => isSupport || hasPermission(permission);

  return {
    canView: can('SALES_VIEW'),
    canViewHaciendaResponse: can('SALES_HACIENDA_RESPONSE_VIEW'),
    canViewPdf: can('SALES_PDF_VIEW'),
    canSendEmail: can('SALES_SEND_EMAIL'),
    canConsultDte: can('SALES_DTE_CONSULT'),
    canViewJson: can('SALES_JSON_VIEW'),
    canReprint: can('SALES_REPRINT'),
    canCancel: can('SALES_VOID'),
    canEdit: can('SALES_EDIT'),
    canEditFinalized: can('SALES_EDIT_FINALIZED'),
    canResendDte: can('SALES_RESEND_DTE'),
    canForceAnnul: can('SALES_VOID'),
    canDeleteRejected: can('SALES_VOID'),
    canEditPendingCashier: can('SALES_EDIT'),
    canSupportView: hasPermission('SALE_SUPPORT_VIEW'),
    canSupportEdit: hasPermission('SALE_SUPPORT_EDIT'),
    canCreateReturn: isSupport || hasRole('ADMIN'),
  };
};
